import {
  afterNextRender,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  Injector,
  OnDestroy,
  OnInit,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import { DOCUMENT, ViewportScroller } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { Event, NavigationEnd, NavigationStart, Router } from '@angular/router';
import { DEFAULT_INTERRUPTSOURCES, Idle } from '@ng-idle/core';
import { Keepalive } from '@ng-idle/keepalive';
import { Store } from '@ngrx/store';
import { Observable, Subject, takeUntil } from 'rxjs';

import * as fromStore from 'src/app/store/';
import { AuthService } from './core/services/auth';
import { StateService } from './core/services/state';
import { User } from './models/user.model';
import { SessionExpiredComponent } from './shared/components';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AppComponent implements OnInit, OnDestroy {
  title: string = 'MyPortal';

  loading = signal(true);

  permissions: string[] = [];

  // How long a user can be inactive before considered idle, in seconds.
  sessionLengthInSeconds: number = 900; // 15 minutes.

  // How long a user can be idle before considered timed out, in seconds.
  timeoutWarningSeconds: number = 60; // 1 minute.

  timedOut = signal(false);

  lastPing: Date = null;

  private store = inject(Store<fromStore.State>);

  loggedInUser$: Observable<User>;

  displayLoadingSpinner = signal(false);

  private destroy$ = new Subject<void>();

  private dialog = inject(MatDialog);

  private idle = inject(Idle);

  private keepAlive = inject(Keepalive);

  private router = inject(Router);

  private state = inject(StateService);

  private authService = inject(AuthService);

  private document = inject(DOCUMENT);

  private viewportScroller = inject(ViewportScroller);

  private injector = inject(Injector);

  private previousPath: string | null = null;

  private isPopstateNavigation = false;

  async ngOnInit(): Promise<void> {
    this.router.events
      .pipe(takeUntil(this.destroy$))
      .subscribe((event: Event) => {
        if (event instanceof NavigationStart) {
          this.isPopstateNavigation = event.navigationTrigger === 'popstate';
        }

        if (event instanceof NavigationEnd) {
          this.handleScrollOnNavigation(event.urlAfterRedirects);
          this.moveFocusToMainContent();
        }
      });

    this.initSessionTimeouts();
    this.state
      .getState()
      .pipe(takeUntil(this.destroy$))
      .subscribe((overlayState: string) => {
        if (overlayState === 'show_loading_overlay') {
          this.displayLoadingSpinner.set(true);
        } else {
          this.displayLoadingSpinner.set(false);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Replaces the router's built-in `scrollPositionRestoration`, which scrolled
   * to the top on every navigation, including query-param-only changes such as
   * list filters and grouping. Back/forward is left to the browser.
   */
  private handleScrollOnNavigation(urlAfterRedirects: string): void {
    const path = urlAfterRedirects.split(/[?#]/)[0];
    const pathChanged = path !== this.previousPath;

    this.previousPath = path;

    if (pathChanged && !this.isPopstateNavigation) {
      this.viewportScroller.scrollToPosition([0, 0]);
    }
  }

  /**
   * WCAG 2.4.3: after each route change, move focus to the main content
   * landmark so keyboard/screen-reader users start from the new page rather
   * than a stale position. `#main-content` is focusable via tabindex="-1".
   */
  private moveFocusToMainContent(): void {
    afterNextRender(
      () => {
        const main = this.document.getElementById('main-content');
        main?.focus({ preventScroll: true });
      },
      { injector: this.injector },
    );
  }

  initSessionTimeouts(): void {
    // Set idle and timeout limits.
    // See if there is an expires entry in local storage
    const expires = localStorage.getItem('ng2Idle.main.expiry');
    if (expires) {
      const timestamp = Number(expires);
      const expireDate = new Date(timestamp);
      const remainingSession = Math.floor(
        (expireDate.getTime() - Date.now()) / 1000,
      );

      // If the user has time left in their session from a previous visit or another tab
      // Set the timeout from localstorage
      if (remainingSession > 0) {
        this.idle.setIdle(remainingSession);
      } else {
        this.idle.setIdle(this.sessionLengthInSeconds);
      }
    } else {
      this.idle.setIdle(this.sessionLengthInSeconds);
    }

    this.idle.setTimeout(this.timeoutWarningSeconds);

    // sets the default interrupts, in this case, things like clicks, scrolls, touches to the document
    this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);

    // No longer idle
    this.idle.onIdleEnd.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.resetSession();
    });

    // Timed out
    this.idle.onTimeout.pipe(takeUntil(this.destroy$)).subscribe(async () => {
      this.timedOut.set(true);
      this.dialog.closeAll();
      await this.authService.signOut('expired');
    });

    // User is idle
    this.idle.onIdleStart.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.idle.clearInterrupts();
      this.openDialog();
    });

    // dialog countdown start
    this.idle.onTimeoutWarning
      .pipe(takeUntil(this.destroy$))
      .subscribe((/* countdown */) => {});

    // sets the ping interval
    this.keepAlive.interval(this.sessionLengthInSeconds);
    this.keepAlive.onPing.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.lastPing = new Date();
      return true;
    });
  }

  private resetSession(): void {
    this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);
    this.idle.watch();
    this.timedOut.set(false);
  }

  openDialog(): void {
    const dialogConfig = {
      autoFocus: true,
      minHeight: '200px',
      maxHeight: 'calc(100vh - 20px)',
      width: '560px',
      maxWidth: 'calc(100vw - 20px)',
      data: {},
      disableClose: true,
    };

    const sessionExpiredDialog = this.dialog.open(
      SessionExpiredComponent,
      dialogConfig,
    );

    sessionExpiredDialog
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe(async (staySignedIn: boolean) => {
        if (staySignedIn) {
          this.resetSession();
        } else {
          await this.authService.signOut('expired');
        }
      });
  }
}
